import { useCallback } from 'react'
import { useMailStore } from '../stores/mailStore'
import { useAccountStore } from '../stores/accountStore'
import type { Thread } from '../lib/types'

/**
 * Returns a `trashThread(thread)` callback that:
 * 1. Optimistically removes the thread from local state immediately.
 * 2. Moves all emails in the thread to the Trash mailbox via JMAP.
 */
export function useTrashThread() {
  const { trashThread: localTrash } = useMailStore()

  return useCallback((thread: Pick<Thread, 'id' | 'labels'>) => {
    // 1. Optimistic local update — thread disappears from list immediately
    localTrash(thread.id)

    // 2. JMAP move to Trash
    const client = useAccountStore.getState().getActiveClient()
    if (!client) return

    // Get trash mailbox ID directly from the account store (always up-to-date)
    const mailboxes = useAccountStore.getState().getActiveMailboxes()
    const trashMb = client.findMailboxByRole(mailboxes, 'trash')
    if (!trashMb) return

    // Remove from current mailboxes
    const removeIds = thread.labels
      .map((l) => l.id)
      .filter((id) => !id.startsWith('virtual:') && id !== trashMb.id)

    // Move to Trash + set $deleted so IMAP sees \Deleted flag
    client.moveThreadToMailbox(thread.id, trashMb.id, removeIds, { '$deleted': true }).catch(() => {})
  }, [localTrash])
}
